import { Component } from "vue";
import { SectionType } from "@/apps/page/page.interfaces";
import {
    BlockBaseElement,
    BlockText,
    BlockLink,
    BlockButton,
    BlockHeading,
    BlockImage,
    BlockDescriptiveImage,
    BlockBackgroundImage,
    BlockArrayItems,
    BlockDataValue,
} from "@/views/blocks/PageBlock.interface";
import {
    BlockDefinitionField,
    BlockDefinitionGroup,
    BlockDefinitionTextField,
    BlockDefinitionHtmlField,
    BlockDefinitionSelectField,
    BlockDefinitionSliderField,
    BlockDefinitionImageField,
    BlockDef,
    BlockColorField,
} from "./block.interfaces";
import { randomId } from "@/shared/utils/helpers";

interface BlockFieldConfig<T> {
    label: string;
    group: BlockDefinitionGroup;
    fields: BlockDefinitionField[];
    defaultData: BlockBaseElement<T>;
}

function createBlock<T extends Record<string, BlockBaseElement<BlockDataValue>>>(config: {
    typeId: string;
    type: SectionType;
    name: string;
    component: Component;
}) {
    const dataFields: Array<{
        value: string;
        label: string;
        group: BlockDefinitionGroup;
        groupType: "single" | "multiple";
        fields: BlockDefinitionField[];
        defaultData: BlockBaseElement<BlockDataValue>;
    }> = [];

    const styleFields: BlockColorField[] = [];

    return {
        withField<K extends keyof T>(
            key: K,
            field: BlockFieldConfig<
                T[K] extends BlockBaseElement<infer U> ? U & BlockDataValue : never
            >
        ) {
            dataFields.push({
                value: key as string,
                ...field,
                groupType: "single",
            });
            return this;
        },

        withArrayField<K extends keyof T>(
            key: K,
            field: BlockFieldConfig<
                T[K] extends BlockBaseElement<infer U> ? U & BlockDataValue : never
            >
        ) {
            dataFields.push({
                value: key as string,
                ...field,
                groupType: "multiple",
            });
            return this;
        },

        withStyleFields(fields: BlockColorField[]) {
            styleFields.push(...fields);
            return this;
        },

        build(): BlockDef {
            return {
                typeId: config.typeId,
                type: config.type,
                name: config.name,
                // @ts-expect-error TODO BF: fix typing
                dataFields,
                styleFields,
                component: config.component,
            };
        },
    };
}

function textField(
    label: string,
    defaultText: string,
    fieldLabel?: string
): BlockFieldConfig<BlockText> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "text",
                label: fieldLabel ?? label,
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: { text: defaultText },
            isHidden: false,
        },
    };
}

function htmlField(
    label: string,
    defaultText: string,
    fieldLabel?: string,
    rows = 5
): BlockFieldConfig<BlockText> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "html",
                label: fieldLabel ?? label,
                rows,
            } satisfies BlockDefinitionHtmlField,
        ],
        defaultData: {
            data: { text: defaultText },
            isHidden: false,
        },
    };
}

function headingField(
    label: string,
    defaultText: string,
    defaultTag: BlockHeading["tag"] = "h2"
): BlockFieldConfig<BlockHeading> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "text",
                label: "Text",
            } satisfies BlockDefinitionTextField,
            {
                value: "tag",
                type: "select",
                label: "Tag",
                options: [
                    { value: "h1", label: "H1" },
                    { value: "h2", label: "H2" },
                    { value: "h3", label: "H3" },
                    { value: "h4", label: "H4" },
                    { value: "h5", label: "H5" },
                    { value: "h6", label: "H6" },
                ],
            } satisfies BlockDefinitionSelectField,
        ],
        defaultData: {
            data: { text: defaultText, tag: defaultTag },
            isHidden: false,
        },
    };
}

function buttonField(label: string, defaultButton: BlockButton): BlockFieldConfig<BlockButton> {
    return {
        label,
        group: "buttons",
        fields: [
            {
                value: "label",
                type: "text",
                label: "Label",
            } satisfies BlockDefinitionTextField,
            {
                value: "href",
                type: "text",
                label: "Link",
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: defaultButton,
            isHidden: false,
        },
    };
}

function imageField(label: string, defaultImage: BlockImage): BlockFieldConfig<BlockImage> {
    return {
        label,
        group: "images",
        fields: [
            {
                value: "src",
                type: "image",
                label: "Image",
            } satisfies BlockDefinitionImageField,
        ],
        defaultData: {
            data: defaultImage,
            isHidden: false,
        },
    };
}

function descriptiveImageField(
    label: string,
    defaultImage: BlockDescriptiveImage
): BlockFieldConfig<BlockDescriptiveImage> {
    return {
        label,
        group: "images",
        fields: [
            {
                value: "src",
                type: "image",
                label: "Image",
            } satisfies BlockDefinitionImageField,
            {
                value: "alt",
                type: "text",
                label: "Alt",
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: defaultImage,
            isHidden: false,
        },
    };
}

function backgroundImageField(
    label: string,
    defaultImage: BlockBackgroundImage
): BlockFieldConfig<BlockBackgroundImage> {
    return {
        label,
        group: "images",
        fields: [
            {
                value: "src",
                type: "image",
                label: "Image",
            } satisfies BlockDefinitionImageField,
            {
                value: "transparency",
                type: "slider",
                label: "Transparency",
                min: 0,
                max: 100,
            } satisfies BlockDefinitionSliderField,
        ],
        defaultData: {
            data: defaultImage,
            isHidden: false,
        },
    };
}

function linkTextField(
    label: string,
    defaultData: BlockLink & BlockText
): BlockFieldConfig<BlockLink & BlockText> {
    return {
        label,
        group: "texts",
        fields: [
            {
                value: "text",
                type: "text",
                label: "Text",
            } satisfies BlockDefinitionTextField,
            {
                value: "label",
                type: "text",
                label: "Link label",
            } satisfies BlockDefinitionTextField,
            {
                value: "href",
                type: "text",
                label: "Link href",
            } satisfies BlockDefinitionTextField,
        ],
        defaultData: {
            data: defaultData,
            isHidden: false,
        },
    };
}

function arrayField<T extends Record<string, string>>(
    label: string,
    group: BlockDefinitionGroup,
    fields: {
        [K in keyof T]: {
            label: string;
            type?: string;
            options?: { value: string; label: string }[];
        };
    },
    defaultItems: T[]
): BlockFieldConfig<BlockArrayItems<T>> {
    return {
        label,
        group,
        fields: Object.entries(fields).map(([key, value]) => ({
            value: key,
            type: value.type ?? "text",
            label: value.label,
            ...(value.type === "select" && {
                options: value.options,
            }),
        })) as BlockDefinitionField[],
        defaultData: {
            data: {
                items: defaultItems.map((item) => ({
                    ...item,
                    id: randomId(),
                })),
            },
            isHidden: false,
        },
    };
}

export {
    createBlock,
    textField,
    htmlField,
    headingField,
    buttonField,
    imageField,
    descriptiveImageField,
    backgroundImageField,
    linkTextField,
    arrayField,
};
